import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/userService';
import { ProfessionAutocomplete } from '../ui/ProfessionAutocomplete';

export const UserProfilePage: React.FC = () => {
  const { userProfile, user, setUserProfile, clearAuth } = useAuthStore();
  const addNotification = useNotificationStore(s => s.addNotification);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: userProfile?.name || '',
    email: userProfile?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    emailNotifications: userProfile?.preferences?.emailNotifications ?? true,
    pushNotifications: userProfile?.preferences?.pushNotifications ?? true,
    // Job preferences
    positions: userProfile?.jobPreferences?.positions || [],
    description: userProfile?.jobPreferences?.description || '',
    experienceLevel: userProfile?.jobPreferences?.experienceLevel || 'Mid',
    preferredSector: userProfile?.jobPreferences?.preferredSector || '',
    remotePreference: userProfile?.jobPreferences?.remotePreference || 'Flexible',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [professionSearch, setProfessionSearch] = useState('');

  // Sync form with userProfile changes
  useEffect(() => {
    if (userProfile) {
      setForm({
        name: userProfile.name,
        email: userProfile.email,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        emailNotifications: userProfile.preferences?.emailNotifications ?? true,
        pushNotifications: userProfile.preferences?.pushNotifications ?? true,
        // Job preferences
        positions: userProfile.jobPreferences?.positions || [],
        description: userProfile.jobPreferences?.description || '',
        experienceLevel: userProfile.jobPreferences?.experienceLevel || 'Mid',
        preferredSector: userProfile.jobPreferences?.preferredSector || '',
        remotePreference: userProfile.jobPreferences?.remotePreference || 'Flexible',
      });
    }
  }, [userProfile]);

  if (!userProfile) return <div className="p-8 text-center">Caricamento profilo...</div>;

  const isDemo = user && (user as any).demo;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePositionTag = (tag: string) => {
    setForm(f => ({
      ...f,
      positions: f.positions.includes(tag) 
        ? f.positions.filter(p => p !== tag)
        : [...f.positions, tag]
    }));
  };

  const addCustomPosition = (position: string) => {
    if (position.trim() && !form.positions.includes(position.trim())) {
      setForm(f => ({
        ...f,
        positions: [...f.positions, position.trim()]
      }));
      setProfessionSearch('');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.name.trim()) return setError('Il nome è obbligatorio.');
    if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return setError('Email non valida.');
    if (form.newPassword && form.newPassword.length < 6) return setError('La nuova password deve essere di almeno 6 caratteri.');
    if (form.newPassword && form.newPassword !== form.confirmPassword) return setError('Le password non coincidono.');
    setLoading(true);
    try {
      if (isDemo) {
        // Aggiorna solo localStorage e stato
        const demoUser = JSON.parse(localStorage.getItem('demoUser') || '{}');
        demoUser.name = form.name;
        demoUser.email = form.email;
        localStorage.setItem('demoUser', JSON.stringify(demoUser));
        setUserProfile({ ...userProfile, name: form.name, email: form.email, preferences: { emailNotifications: form.emailNotifications, pushNotifications: form.pushNotifications } });
        setSuccess('Profilo aggiornato! (demo)');
        addNotification({ type: 'info', title: 'Profilo aggiornato', message: 'Le modifiche sono state salvate (demo).' });
      } else {
        // Aggiorna su Firebase
        if (!user?.uid) throw new Error('Utente non autenticato');
        
        // Aggiorna nome se cambiato
        if (form.name !== userProfile.name) {
          await userService.updateUserName(user, form.name);
        }
        
        // Aggiorna email se cambiata
        if (form.email !== userProfile.email) {
          if (!form.currentPassword) {
            throw new Error('Password attuale richiesta per cambiare email');
          }
          await userService.updateUserEmail(user, form.email, form.currentPassword);
        }
        
        // Aggiorna password se fornita
        if (form.newPassword) {
          if (!form.currentPassword) {
            throw new Error('Password attuale richiesta per cambiare password');
          }
          await userService.updateUserPassword(user, form.currentPassword, form.newPassword);
        }
        
        // Aggiorna preferenze
        const updatedProfile = await userService.updateUserProfile(user.uid, {
          preferences: {
            emailNotifications: form.emailNotifications,
            pushNotifications: form.pushNotifications
          },
          jobPreferences: {
            positions: form.positions,
            description: form.description,
            experienceLevel: form.experienceLevel,
            preferredSector: form.preferredSector,
            remotePreference: form.remotePreference
          }
        });
        
        // Aggiorna lo stato locale
        setUserProfile(updatedProfile);
        
        // Reset password fields
        setForm(f => ({
          ...f,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
        
        setSuccess('Profilo aggiornato con successo!');
        addNotification({ type: 'info', title: 'Profilo aggiornato', message: 'Le modifiche sono state salvate.' });
      }
    } catch (err: any) {
      setError(err.message || 'Errore durante il salvataggio.');
      addNotification({ type: 'warning', title: 'Errore', message: err.message || 'Errore durante il salvataggio.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setError(''); setSuccess('');
    if (!window.confirm('Sei sicuro di voler eliminare il tuo account? Questa azione è irreversibile.')) return;
    setLoading(true);
    try {
      if (isDemo) {
        localStorage.removeItem('demoUser');
        clearAuth();
        navigate('/auth');
      } else {
        // Elimina profilo da Firestore
        if (!user?.uid) throw new Error('Utente non autenticato');
        await userService.deleteUserProfile(user.uid);
        
        // Logout e redirect
        clearAuth();
        navigate('/auth');
        addNotification({ type: 'info', title: 'Account eliminato', message: 'Il tuo account è stato eliminato con successo.' });
      }
    } catch (err: any) {
      setError(err.message || 'Errore durante eliminazione account.');
      addNotification({ type: 'warning', title: 'Errore', message: err.message || 'Errore durante eliminazione account.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Profilo Utente</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">Gestisci le tue informazioni personali e preferenze</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900 dark:text-white">{userProfile.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{userProfile.email}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-300 font-semibold text-lg">
                {userProfile.name.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-bold mb-6 text-gray-900 dark:text-white">Informazioni Personali</h2>
      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Nome</label>
          <input name="name" value={form.name} onChange={handleChange} className="input w-full dark:bg-gray-700 dark:text-white dark:border-gray-600" autoComplete="name" required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Email</label>
          <input name="email" value={form.email} onChange={handleChange} className="input w-full dark:bg-gray-700 dark:text-white dark:border-gray-600" autoComplete="email" required type="email" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Password attuale</label>
            <input name="currentPassword" value={form.currentPassword} onChange={handleChange} className="input w-full dark:bg-gray-700 dark:text-white dark:border-gray-600" type="password" autoComplete="current-password" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Nuova password</label>
            <input name="newPassword" value={form.newPassword} onChange={handleChange} className="input w-full dark:bg-gray-700 dark:text-white dark:border-gray-600" type="password" autoComplete="new-password" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Conferma nuova password</label>
            <input name="confirmPassword" value={form.confirmPassword} onChange={handleChange} className="input w-full dark:bg-gray-700 dark:text-white dark:border-gray-600" type="password" autoComplete="new-password" />
          </div>
        </div>
        <div className="flex items-center gap-4 mt-2">
          <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <input type="checkbox" name="emailNotifications" checked={form.emailNotifications} onChange={handleChange} />
            Email notifications
          </label>
          <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <input type="checkbox" name="pushNotifications" checked={form.pushNotifications} onChange={handleChange} />
            Push notifications
          </label>
        </div>

        {/* Job Preferences Section */}
        <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">🎯 Preferenze di Ricerca Lavoro</h3>
          
          {/* Posizioni Ricercate */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              💼 Posizioni Ricercate
            </label>
            <div className="space-y-2">
              {/* Autocompletamento Professioni */}
              <ProfessionAutocomplete
                value={professionSearch}
                onChange={setProfessionSearch}
                onAdd={addCustomPosition}
                placeholder="Cerca una professione del Ticino..."
                className="mb-3"
              />
              
              {/* Suggerimenti Rapidi */}
              <div className="mb-3">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Suggerimenti rapidi:</p>
                <div className="flex flex-wrap gap-2">
                  {['Informatico/a', 'Infermiere/a', 'Cuoco/a', 'Impiegato/a di commercio', 'Meccanico/a di manutenzione per automobili'].map(tag => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => handlePositionTag(tag)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        form.positions.includes(tag)
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Posizioni Selezionate */}
              {form.positions.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Posizioni selezionate:</p>
                  <div className="flex flex-wrap gap-2">
                    {form.positions.map(position => (
                      <span
                        key={position}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-md text-sm"
                      >
                        {position}
                        <button
                          type="button"
                          onClick={() => handlePositionTag(position)}
                          className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Grid per altri campi */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Livello di Esperienza */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                📈 Livello di Esperienza
              </label>
              <select
                name="experienceLevel"
                value={form.experienceLevel}
                onChange={handleChange}
                className="input w-full dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                <option value="Junior">Junior (0-2 anni)</option>
                <option value="Mid">Mid (3-5 anni)</option>
                <option value="Senior">Senior (6-8 anni)</option>
                <option value="Lead">Lead (9-12 anni)</option>
                <option value="Manager">Manager (13+ anni)</option>
              </select>
            </div>

            {/* Preferenza Remote */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                🏠 Preferenza Lavoro
              </label>
              <select
                name="remotePreference"
                value={form.remotePreference}
                onChange={handleChange}
                className="input w-full dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                <option value="On-site">Solo in ufficio</option>
                <option value="Hybrid">Ibrido</option>
                <option value="Remote">Solo remoto</option>
                <option value="Flexible">Flessibile</option>
              </select>
            </div>
          </div>

          {/* Settore Preferito */}
          <div className="mt-4">
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              🏢 Settore Preferito
            </label>
            <input
              name="preferredSector"
              value={form.preferredSector}
              onChange={handleChange}
              className="input w-full dark:bg-gray-700 dark:text-white dark:border-gray-600"
              placeholder="es. Tecnologia, Finanza, Healthcare, Marketing..."
            />
          </div>

          {/* Descrizione Dettagliata */}
          <div className="mt-4">
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              📝 Descrizione Dettagliata
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={4}
              className="input w-full dark:bg-gray-700 dark:text-white dark:border-gray-600 resize-none"
              placeholder="Descrivi in dettaglio le posizioni che stai cercando, le tue competenze, esperienze specifiche, obiettivi di carriera..."
            />
          </div>
        </div>
        {error && <div className="text-red-600 dark:text-red-400 text-sm mt-2">{error}</div>}
        {success && <div className="text-green-600 dark:text-green-400 text-sm mt-2">{success}</div>}
        <button type="submit" className="btn-primary w-full mt-2" disabled={loading}>{loading ? 'Salvataggio...' : 'Salva modifiche'}</button>
      </form>
      
      <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-4">
        <button onClick={handleDelete} className="w-full text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg py-2 font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 transition" disabled={loading}>
          Elimina account
        </button>
      </div>
      </div>
    </div>
  );
}; 